import { useRef, useMemo, useEffect, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import './editor.css';
import { useDebounce } from '../hooks/useDebounce';
import EditorSidebarSection from '../components/shared/EditorSidebarSection';
import EditorTopBar from '../components/EditorTopBar';
import CitationCard from '../components/CitationCard';
import { useSearchParams, useParams, useBlocker } from 'react-router-dom';
import Button from '../components/shared/Button';
import AddCitationModal from '../components/modals/add-citation/addCitationModal';
import ConfirmationModal from '../components/modals/confirmationModal';
import { ChevronUp, AlertTriangle } from 'lucide-react';
import { DocumentService } from '../services/DocumentService';
import { CitationService } from '../services/CitationService';
import {
	mapBackendToFrontend,
	mapBackendToFormFields,
	getFrontendSourceType
} from '../utils/citationFieldMapper';
import { generateInText } from '../utils/citationGenerator';
import { formatLocatorValue } from '../utils/citationLocatorHelper';
import { Toast } from '../components/shared/Toast';
import Loader from '../components/shared/Loader';
import DeleteIcon from '../../src/assets/icons/delete.svg';
import CitationDetailModal from '../components/modals/add-citation/citationDetailModal';
import Tooltip from '../components/shared/Tooltip';
import { generateCitationForDisplay } from '../utils/citationDisplayHelper';
import PDFPreviewModal from '../components/modals/PDFPreviewModal';
import { API_BASE_URL } from '../utils/constants';
import axiosInstance from '../services/axiosInstance';
import { getExportPdfOptions } from '../utils/pdfExportUtils';


// TinyMCE API Key from environment (Create React App requires REACT_APP_ prefix)
const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

const DocumentEditor = () => {
	const [initialContent, setInitialContent] = useState('');
	const [editorReady, setEditorReady] = useState(false);
	const [doc, setDoc] = useState(null);
	const [loading, setLoading] = useState(false);
	const [sidebarSections, setSidebarSections] = useState([]);
	const [query, setQuery] = useState('');
	const [isCitationModalOpen, setIsCitationModalOpen] = useState(false);
	// const [isExporting, setIsExporting] = useState(false);
	const [citations, setCitations] = useState([]);
	const [selectedCitation, setSelectedCitation] = useState(null);
	const [isSourceLibraryExpanded, setIsSourceLibraryExpanded] = useState(true);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isDeletingCitation, setIsDeletingCitation] = useState(false);
	const [citationToDelete, setCitationToDelete] = useState(null);
	const [editingCitation, setEditingCitation] = useState(null);
	const [citationModalMode, setCitationModalMode] = useState('add'); // "add" | "edit" | "insert"
	const [citationToInsert, setCitationToInsert] = useState(null);
	const [isViewDetailsModalOpen, setIsViewDetailsModalOpen] = useState(false);
	const [viewingCitationDetails, setViewingCitationDetails] = useState(null);
	const [savedCursorPosition, setSavedCursorPosition] = useState(null);
	const [isPDFPreviewOpen, setIsPDFPreviewOpen] = useState(false);
	const [pdfBlobForPreview, setPdfBlobForPreview] = useState(null);
	const [savingStatus, setSavingStatus] = useState('saved'); // 'saved', 'saving', 'error'
	const [manualSavingStatus, setManualSavingStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'

	// Refs for image tracking
	const deletedImageUrls = useRef(new Set());
	const currentImageUrls = useRef(new Set());
	const docRef = useRef(doc);

	const editorRef = useRef(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const { id: documentId } = useParams();
	const isCreated = searchParams.get('isCreated') === 'true';
	const [showCitationManager, setShowCitationManager] = useState(isCreated);

	// Track if initial content has been loaded and rendered
	const isInitialLoadComplete = useRef(false);

	// Store previous content state for change detection (reconciliation)
	// Map<sectionId, {content: string, subsections: Map<subId, string>}>
	const previousContentRef = useRef(new Map());

	// Document Information
	const documentTitle = doc?.title || '';
	const documentType = doc?.documentType?.name || '';
	const documentStatus = doc?.status || '';
	const documentEditedTime = doc?.updatedAt
		? new Date(doc.updatedAt).toISOString().slice(0, 10)
		: '';
	const documentFormat = doc?.format?.name || '';
	const documentUniversity = doc?.university?.name || '';

	// Filter citations based on search query
	const filteredCitations = useMemo(() => {
		if (!query.trim()) return citations;

		const searchTerm = query.toLowerCase().trim();

		return citations.filter((citation) => {
			// Search in title
			if (citation.title?.toLowerCase().includes(searchTerm)) return true;

			// Search in various title fields
			if (citation.bookTitle?.toLowerCase().includes(searchTerm)) return true;
			if (citation.articleTitle?.toLowerCase().includes(searchTerm)) return true;
			if (citation.chapterTitle?.toLowerCase().includes(searchTerm)) return true;
			if (citation.episodeTitle?.toLowerCase().includes(searchTerm)) return true;

			// Search in authors
			if (citation.authors && Array.isArray(citation.authors)) {
				const authorMatch = citation.authors.some((author) => {
					if (typeof author === 'string') {
						return author.toLowerCase().includes(searchTerm);
					}
					if (author.name) {
						return author.name.toLowerCase().includes(searchTerm);
					}
					return false;
				});
				if (authorMatch) return true;
			}

			// Search in organization
			if (citation.organization?.toLowerCase().includes(searchTerm)) return true;

			// Search in year
			if (citation.year?.includes(searchTerm)) return true;

			// Search in category and sourceType
			if (citation.category?.toLowerCase().includes(searchTerm)) return true;
			if (citation.sourceType?.toLowerCase().includes(searchTerm)) return true;

			// Search in other name fields
			if (citation.communicatorName?.toLowerCase().includes(searchTerm)) return true;
			if (citation.uploaderOrAuthor?.toLowerCase().includes(searchTerm)) return true;
			if (citation.authorName?.toLowerCase().includes(searchTerm)) return true;

			return false;
		});
	}, [citations, query]);

	const handleEditDocumentClick = () => {
		setSearchParams({ isCreated: true }, { replace: true });
		setShowCitationManager(true);
	};

	function buildHtmlFromFileContent(fileContent) {
		let html = '';
		let isFirstSection = true;

		// Handle category-based structure
		if (fileContent.categories && Array.isArray(fileContent.categories)) {
			fileContent.categories.forEach((category) => {
				if (category.sections && Array.isArray(category.sections)) {
					category.sections.forEach((section) => {
						if (section.optional === false && section.isVisible !== false) {
							// Add page-break-before class to all sections except the first
							const pageBreakClass = isFirstSection ? '' : 'page-break-before';
							isFirstSection = false;

							html += `<div id="${section.id}" class="${pageBreakClass}">
                            ${section.content?.html || ''}
                        `;

							if (section.subsections && Array.isArray(section.subsections)) {
								section.subsections.forEach((sub) => {
									if (sub.optional === false || sub.optional === undefined) {
										html += `
                                        <div id="${sub.id}">
                                            ${sub.content?.html || ''}
                                        </div>
                                    `;
									}
								});
							}

							html += `</div>`;
						}
					});
				}
			});
		}
		// Fallback for old structure
		else if (fileContent.sections && Array.isArray(fileContent.sections)) {
			fileContent.sections.forEach((section, index) => {
				const pageBreakClass = index === 0 ? '' : 'page-break-before';

				html += `<div class="ql-section ${pageBreakClass}" id="${section.id}">
                ${section.content?.html || ''}
                ${section.subsections?.map(
					(sub) => `
                        <div class="ql-subsection" id="${sub.id}">
                            ${sub.content?.html || ''}
                        </div>`
				).join('') || ''}
            </div>`;
			});
		}

		return html;
	}

	// Fetch Document Data
	useEffect(() => {
		if (!documentId) return;
		let mounted = true;

		// Reset the flag when loading a new document
		isInitialLoadComplete.current = false;

		(async () => {
			try {
				setLoading(true);
				const res = await DocumentService.fetchDocumentById(documentId);
				const d = res?.document;
				if (!mounted || !d) return;

				setDoc(d);

				// Load citations from document
				if (d?.citations && Array.isArray(d.citations)) {
					const transformedCitations = d.citations.map((citation) =>
						mapBackendToFrontend(citation)
					);
					setCitations(transformedCitations);
				} else {
					setCitations([]);
				}

				if (d?.fileContent) {
					const html = buildHtmlFromFileContent(d.fileContent);
					setInitialContent(html);

					// Handle category-based structure
					if (d.fileContent.categories && Array.isArray(d.fileContent.categories)) {
						const categoriesData = d.fileContent.categories.map((category) => ({
							id: category.id,
							name: category.name,
							// Included sections (optional: false AND isVisible: true)
							sections: (category.sections || [])
								.filter((section) => section.optional === false && section.isVisible !== false)
								.map((section) => ({
									id: section.id,
									title: section.title,
									// Track if this is an added section (can be deleted)
									// Added sections have ID pattern: originalId-added-timestamp
									canDelete: section.required === false && section.id.includes('-added-'),
									subsections: (section.subsections || [])
										.filter((sub) => sub.optional === false || sub.optional === undefined)
										.map((sub) => ({
											id: sub.id,
											title: sub.title
										}))
								})),
							// Optional sections that can be added (required: false, optional: true, isVisible: true)
							optionalSections: (category.sections || [])
								.filter((section) => section.required === false && section.optional === true && section.isVisible !== false)
								.map((section) => ({
									id: section.id,
									title: section.title
								}))
						}));
						setSidebarSections(categoriesData);
					}
					// Fallback for old structure
					else {
						const sections = Array.isArray(d?.fileContent?.sections) ? d.fileContent.sections : [];
						setSidebarSections(
							sections.map((s) => ({
								id: s.id,
								title: s.title,
								subsections: Array.isArray(s.subsections)
									? s.subsections.map((ss) => ({
										id: ss.id,
										title: ss.title
									}))
									: []
							}))
						);
					}
				}
			} catch (e) {
				const message = e?.response?.data?.message || e?.message || 'Failed to load document';
				Toast.error(message);
			} finally {
				if (mounted) setLoading(false);
			}
		})();

		return () => {
			mounted = false;
		};
	}, [documentId]);

	// Prevent background scrolling when modals are open
	useEffect(() => {
		if (isCitationModalOpen || isViewDetailsModalOpen || isPDFPreviewOpen || isDeleteModalOpen) {
			// Disable scrolling on body and main element
			const mainEl = document.querySelector('main');
			const originalBodyOverflow = document.body.style.overflow;
			const originalMainOverflow = mainEl ? mainEl.style.overflow : '';

			document.body.style.overflow = 'hidden';
			if (mainEl) {
				mainEl.style.overflow = 'hidden';
			}

			// Re-enable scrolling when modal closes
			return () => {
				document.body.style.overflow = originalBodyOverflow;
				if (mainEl) {
					mainEl.style.overflow = originalMainOverflow;
				}
			};
		}
	}, [isCitationModalOpen, isViewDetailsModalOpen, isPDFPreviewOpen, isDeleteModalOpen]);

	// Initialize previous content ref for change detection (reconciliation baseline)
	// IMPORTANT: Must use editor's normalized output, not raw HTML, because TinyMCE modifies HTML on load
	const initializePreviousContentFromEditor = () => {
		const editor = editorRef.current;
		if (!editor) return;

		// Get the normalized HTML from the editor (this is what we'll compare against)
		const editorHTML = editor.getContent();
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = editorHTML;

		const sectionDivs = tempDiv.querySelectorAll('[id^="section-"]');
		previousContentRef.current.clear();

		sectionDivs.forEach((sectionDiv) => {
			const sectionId = sectionDiv.getAttribute('id');
			// Match both regular sections (section-27) and added sections (section-27-added-1234567890)
			if (!sectionId || !sectionId.match(/^section-\d+(-added-\d+)?$/)) return;

			// Extract section number (works for both section-27 and section-27-added-123)
			const sectionNumber = sectionId.split('-')[1];
			const sectionClone = sectionDiv.cloneNode(true);

			// For added sections, subsections also have the -added- suffix, so we need to match by prefix
			// Get all subsections that belong to this section (either subsection-27-X or subsection-27-X-added-Y)
			const subsectionDivs = sectionClone.querySelectorAll(`[id^="subsection-${sectionNumber}-"]`);
			const subsectionsMap = new Map();

			subsectionDivs.forEach((subDiv) => {
				const subId = subDiv.getAttribute('id');
				if (subId) {
					subsectionsMap.set(subId, subDiv.innerHTML.trim());
				}
				subDiv.remove();
			});

			previousContentRef.current.set(sectionId, {
				content: sectionClone.innerHTML.trim(),
				subsections: subsectionsMap
			});
		});

		console.log('📋 Initialized change detection baseline with', previousContentRef.current.size, 'sections');
	};

	// Set initial content when editor is ready and content is loaded
	useEffect(() => {
		if (!editorRef.current || !initialContent || !editorReady) return;

		// Only set content once on initial load
		if (!isInitialLoadComplete.current) {
			const editor = editorRef.current;
			editor.setContent(initialContent);

			// Clear undo history so the user can't undo back to the empty state
			// This makes the loaded content the "starting point"
			editor.undoManager.clear();

			// Wait for TinyMCE to fully process/normalize the content, then initialize baseline
			// This ensures we compare against TinyMCE's normalized output, not the raw HTML
			setTimeout(() => {
				initializePreviousContentFromEditor();

				isInitialLoadComplete.current = true;

				// Set editor editable state after content is fully loaded
				if (editorRef.current) {
					if (isCreated) {
						editorRef.current.mode.set('design');
					} else {
						editorRef.current.mode.set('readonly');
					}
				}
			}, 200); // Slightly longer delay to ensure TinyMCE finishes processing
		}
	}, [initialContent, editorReady, isCreated]);

	useEffect(() => {
		if (doc) {
			docRef.current = doc;
		}
	}, [doc]);

	// Enable/Disable editor based on isCreated state changes (after initial load)
	useEffect(() => {
		// Only run this if initial load is complete
		if (!isInitialLoadComplete.current) return;
		if (!editorRef.current) return;

		if (isCreated) {
			editorRef.current.mode.set('design');
		} else {
			editorRef.current.mode.set('readonly');
		}
	}, [isCreated]);

	// Function to build updated document data from TinyMCE editor content
	// Implements reconciliation - only updates sections/subsections that have changed
	const buildUpdatedDocumentData = () => {
		try {
			const editor = editorRef.current;
			if (!editor) return null;

			const editorHTML = editor.getContent();

			// Create a temporary div to parse the HTML
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = editorHTML;

			// Find all section divs by ID pattern (section-X)
			const sectionDivs = tempDiv.querySelectorAll('[id^="section-"]');

			// Map to store extracted content by section ID
			const extractedSections = new Map();

			// Track what changed (reconciliation)
			const changes = {
				hasChanges: false,
				changedSections: [] // [{sectionId, contentChanged, changedSubsections: []}]
			};

			sectionDivs.forEach((sectionDiv) => {
				const sectionId = sectionDiv.getAttribute('id');
				// Match both regular sections (section-27) and added sections (section-27-added-1234567890)
				if (!sectionId || !sectionId.match(/^section-\d+(-added-\d+)?$/)) return;

				// Extract section number (works for both section-27 and section-27-added-123)
				const sectionNumber = sectionId.split('-')[1];

				// Clone the section div to work with
				const sectionClone = sectionDiv.cloneNode(true);

				// Find all subsections within this section
				// For added sections, subsections also have the -added- suffix
				const subsectionDivs = sectionClone.querySelectorAll(`[id^="subsection-${sectionNumber}-"]`);
				const subsections = [];
				const subsectionsMap = new Map(); // For change detection

				subsectionDivs.forEach((subDiv) => {
					const subId = subDiv.getAttribute('id');
					if (!subId) return;

					const subContent = subDiv.innerHTML.trim();

					// Get the innerHTML of the subsection (its content)
					subsections.push({
						id: subId,
						content: subContent
					});

					// Store in map for change detection
					subsectionsMap.set(subId, subContent);

					// Remove subsection from clone to get section-only content
					subDiv.remove();
				});

				// Sort subsections by their number (handle both regular and -added- patterns)
				subsections.sort((a, b) => {
					const aNum = parseInt(a.id.split('-')[2]);
					const bNum = parseInt(b.id.split('-')[2]);
					return aNum - bNum;
				});

				// What remains in sectionClone is section content (without subsections)
				const sectionContent = sectionClone.innerHTML.trim();

				extractedSections.set(sectionId, {
					content: sectionContent,
					subsections: subsections
				});

				// --- CHANGE DETECTION (Reconciliation) ---
				const previousSection = previousContentRef.current.get(sectionId);

				// Check if section content changed (direct string comparison - 100% accurate)
				const sectionContentChanged = !previousSection || previousSection.content !== sectionContent;

				// Check which subsections changed
				const changedSubsections = [];
				subsections.forEach((sub) => {
					const prevSubContent = previousSection?.subsections?.get(sub.id);
					if (prevSubContent !== sub.content) {
						changedSubsections.push(sub.id);
					}
				});

				// Record changes if any
				if (sectionContentChanged || changedSubsections.length > 0) {
					changes.hasChanges = true;
					changes.changedSections.push({
						sectionId,
						contentChanged: sectionContentChanged,
						changedSubsections
					});
				}

				// Update previous content ref for next comparison
				previousContentRef.current.set(sectionId, {
					content: sectionContent,
					subsections: subsectionsMap
				});
			});

			// If no changes detected, return null to skip update
			if (!changes.hasChanges) {
				console.log('📋 No changes detected, skipping update');
				return null;
			}

			console.log('📝 Changes detected:', changes.changedSections.map(c => ({
				section: c.sectionId,
				contentChanged: c.contentChanged,
				subsectionsChanged: c.changedSubsections.length
			})));

			// Check if using new category-based structure
			if (doc?.fileContent?.categories && Array.isArray(doc.fileContent.categories)) {
				// Build updated categories - only update changed sections
				const updatedCategories = doc.fileContent.categories.map((category) => {
					const updatedSections = category.sections.map((section) => {
						// Check if this section has any changes
						const sectionChange = changes.changedSections.find(c => c.sectionId === section.id);

						if (!sectionChange) {
							return section; // No changes, return as-is
						}

						const extracted = extractedSections.get(section.id);
						if (!extracted) return section;

						// Update subsections - only the ones that changed
						const updatedSubsections = (section.subsections || []).map((sub) => {
							// Check if this specific subsection changed
							if (!sectionChange.changedSubsections.includes(sub.id)) {
								return sub; // No change, return as-is
							}

							const extractedSub = extracted.subsections.find(s => s.id === sub.id);
							return {
								...sub,
								content: {
									html: extractedSub?.content || sub.content?.html || ''
								}
							};
						});

						return {
							...section,
							// Only update section content if it changed
							content: sectionChange.contentChanged
								? { html: extracted.content }
								: section.content,
							subsections: updatedSubsections
						};
					});

					return {
						...category,
						sections: updatedSections
					};
				});

				return {
					...doc,
					fileContent: {
						...doc.fileContent,
						categories: updatedCategories
					},
					_changes: changes // Include change metadata
				};
			}
			// Fallback for old structure
			else {
				const sections = [];

				extractedSections.forEach((extracted, sectionId) => {
					// Check if this section has changes
					const sectionChange = changes.changedSections.find(c => c.sectionId === sectionId);

					// Find the section title from the original doc
					const originalSection = doc?.fileContent?.sections?.find((s) => s.id === sectionId);

					const subsections = extracted.subsections.map((sub) => {
						const originalSubsection = originalSection?.subsections?.find((ss) => ss.id === sub.id);

						// Check if this subsection changed
						const subChanged = sectionChange?.changedSubsections.includes(sub.id);

						return {
							id: sub.id,
							title: originalSubsection?.title || `Subsection ${sub.id}`,
							content: {
								html: subChanged ? sub.content : (originalSubsection?.content?.html || sub.content)
							},
							pageRange: {
								start: null,
								end: null
							}
						};
					});

					sections.push({
						id: sectionId,
						title: originalSection?.title || `Section ${sectionId}`,
						content: {
							html: sectionChange?.contentChanged ? extracted.content : (originalSection?.content?.html || extracted.content)
						},
						subsections: subsections,
						pageRange: {
							start: null,
							end: null
						}
					});
				});

				// Sort sections by their number
				sections.sort((a, b) => {
					const aNum = parseInt(a.id.split('-')[1]);
					const bNum = parseInt(b.id.split('-')[1]);
					return aNum - bNum;
				});

				return {
					...doc,
					fileContent: {
						...doc.fileContent,
						sections: sections
					},
					_changes: changes // Include change metadata
				};
			}
		} catch (error) {
			console.error('Error building updated document data:', error);
			return null;
		}
	};

	const function1 = async () => {
		const updatedData = buildUpdatedDocumentData();

		// Skip if no changes detected (reconciliation optimization)
		if (!updatedData) {
			return; // No changes, nothing to save
		}

		if (!updatedData.fileContent) {
			console.error('Failed to build updated document data');
			return;
		}

		// Log what changed for debugging
		if (updatedData._changes) {
			const changedCount = updatedData._changes.changedSections.length;
			const subsectionCount = updatedData._changes.changedSections.reduce(
				(sum, s) => sum + s.changedSubsections.length, 0
			);
			console.log(`💾 Saving changes: ${changedCount} section(s), ${subsectionCount} subsection(s)`);
		}

		try {
			await DocumentService.updateDocumentLocalFileContent(documentId, updatedData.fileContent);

			// CRITICAL: Update doc state with the new fileContent so subsequent saves build on top of this
			// Without this, the next save would use stale doc state and overwrite previous changes
			setDoc(prevDoc => ({
				...prevDoc,
				fileContent: updatedData.fileContent
			}));

			console.log('✅ Document updated successfully');
		} catch (error) {
			console.error('❌ Failed to update local document:', error);
		}
	};

	const function2 = async () => {
		console.log('Function 2 executed');
		// Push latest fileContent from IndexedDB to server
		setSavingStatus('saving');
		try {
			await DocumentService.syncDocumentFileContent(documentId);
			console.log('Document synced to server successfully');
			setSavingStatus('saved');
		} catch (error) {
			console.error('Failed to sync document to server:', error);
			setSavingStatus('error');
		}
	};

	// Separate function/state for Manual Save button as requested
	const handleManualSave = async () => {
		console.log('Manual Save Executed');
		setManualSavingStatus('saving');
		// Also update the auto-save indicator to show activity, if desired, or keep separate.
		// User requested separation, but logically "saving" is true for both.
		// We'll keep the auto-save indicator independent or updated to 'saved' on success.

		try {
			await DocumentService.syncDocumentFileContent(documentId);
			console.log('Document manually synced to server successfully');

			// Handle Batch Image Deletion
			if (deletedImageUrls.current.size > 0) {
				const urlsToDelete = Array.from(deletedImageUrls.current);
				console.log('🗑️ Processing batch image deletion for:', urlsToDelete);
				try {
					await DocumentService.deleteDocumentImages(documentId, urlsToDelete);
					console.log('✅ Batch image deletion successful');
					deletedImageUrls.current.clear();
				} catch (imgError) {
					console.error('❌ Failed to delete images:', imgError);
				}
			}

			// Clear undo history to make this the new "starting point" as requested
			if (editorRef.current) {
				editorRef.current.undoManager.clear();
				console.log('Undo history cleared after manual save');
			}

			setManualSavingStatus('saved');
			setSavingStatus('saved'); // Sync the top indicator too

			// Optional: Reset manual status to idle after a delay
			setTimeout(() => {
				setManualSavingStatus('idle');
			}, 2000);
		} catch (error) {
			console.error('Failed to manually sync document:', error);
			setManualSavingStatus('error');
		}
	};

	// Direct save function for structural changes (reordering, etc.)
	// Bypasses reconciliation since we're saving the entire doc state
	// For move up/down: syncs to server IMMEDIATELY after IndexedDB save
	const saveDocumentStructure = async (newFileContent) => {
		if (!newFileContent) {
			console.error('No fileContent to save');
			return;
		}

		try {
			console.log('📂 Saving document structure...');
			await DocumentService.updateDocumentLocalFileContent(documentId, newFileContent);
			console.log('✅ Document structure saved to IndexedDB');

			// For structural changes (move up/down), sync to server immediately
			// Clear any pending timer since we're syncing now
			if (fn2TimerRef.current) {
				clearTimeout(fn2TimerRef.current);
				fn2TimerRef.current = null;
			}

			console.log('🔄 Syncing to server immediately...');
			await function2();

		} catch (error) {
			console.error('❌ Failed to save document structure:', error);
			throw error;
		}
	};

	// Track if function2 has been scheduled
	const fn2TimerRef = useRef(null);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (fn2TimerRef.current) {
				clearTimeout(fn2TimerRef.current);
			}
		};
	}, []);

	const debouncedFn1 = useDebounce(() => {
		// Execute function1 (save to IndexedDB)
		function1().catch(err => console.error('Error in function1:', err));

		// Schedule function2 ONLY if not already scheduled
		// This ensures function2 runs 30 seconds after the FIRST function1 call
		// Subsequent function1 calls won't reset/cancel the timer
		if (!fn2TimerRef.current) {
			console.log('⏱️ Scheduling server sync in 30 seconds...');
			fn2TimerRef.current = setTimeout(() => {
				function2().catch(err => console.error('Error in function2:', err));
				fn2TimerRef.current = null; // Reset after execution so new timer can be scheduled
			}, 30000);
		}
	}, 3000);

	const handleEditorChange = (newContent, editor) => {
		// Image Syncing / Deletion Logic
		try {
			// 1. Extract all current image sources from the editor content
			const doc = new DOMParser().parseFromString(newContent, 'text/html');
			const imgs = doc.querySelectorAll('img');
			const newImageSet = new Set();

			imgs.forEach(img => {
				const src = img.getAttribute('src');
				if (src) newImageSet.add(src);
			});

			// 2. Identify images that were present before but are missing now
			// We only run this check if we have an initial set of images (after first load)
			if (isInitialLoadComplete.current) {
				// 2a. Check for deletions (Old URL not in New Set)
				currentImageUrls.current.forEach(oldUrl => {
					// Ignore temporary blob/data URLs (e.g. during upload)
					if (oldUrl.startsWith('blob:') || oldUrl.startsWith('data:')) {
						return;
					}

					if (!newImageSet.has(oldUrl)) {
						// Image was deleted
						if (!deletedImageUrls.current.has(oldUrl)) {
							deletedImageUrls.current.add(oldUrl);
							console.log('🗑️ Image deleted:', oldUrl);
							console.log('📋 Current deleted images list:', Array.from(deletedImageUrls.current));
						}
					}
				});

				// 2b. Check for restorations/undo (New Set has URL that was in Deleted List)
				newImageSet.forEach(newUrl => {
					if (deletedImageUrls.current.has(newUrl)) {
						// Image was restored (undo/pasted back)
						deletedImageUrls.current.delete(newUrl);
						console.log('♻️ Image restored:', newUrl);
						console.log('📋 Current deleted images list:', Array.from(deletedImageUrls.current));
					}
				});

				// 3. Update current set for next comparison
				currentImageUrls.current = newImageSet;
			} else {
				// Initial population
				currentImageUrls.current = newImageSet;
			}

		} catch (e) {
			console.error('Error tracking image deletions:', e);
		}

		// Don't update state on every change (uncontrolled component pattern)
		// Only call debounced save functions after initial content is loaded
		if (isInitialLoadComplete.current) {
			debouncedFn1();
		}
	};

	// Sidebar scroll
	const handleSidebarNavigate = (id) => {
		try {
			const editor = editorRef.current;
			if (!editor) return;

			// Find the target section/subsection element in the editor body
			const editorBody = editor.getBody();
			const target = editorBody.querySelector(`#${CSS.escape(id)}`);
			if (!target) {
				console.warn('Target element not found:', id);
				return;
			}

			// Find the first content element after/inside the target div
			const firstChild =
				target.querySelector('p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, pre, table') ||
				target.firstElementChild;

			if (!firstChild) {
				// Section exists but is empty - still scroll to it
				target.scrollIntoView({ behavior: 'smooth', block: 'start' });
				return;
			}

			// Scroll the element into view
			firstChild.scrollIntoView({ behavior: 'smooth', block: 'start' });

			// Set cursor at the beginning of the first content element
			editor.selection.setCursorLocation(firstChild, 0);

			// Focus the editor
			editor.focus();
		} catch (err) {
			console.error('Navigation error:', err);
		}
	};

	// Move section up/down within its category
	const handleMoveSection = async (categoryId, sectionId, direction) => {
		if (!doc?.fileContent?.categories) return;

		// Find the category
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const category = doc.fileContent.categories[categoryIndex];
		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;

		// Calculate new index
		const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;

		// Check bounds - can't move beyond array limits
		if (newIndex < 0 || newIndex >= category.sections.length) {
			Toast.info(`Cannot move section ${direction} - already at the ${direction === 'up' ? 'top' : 'bottom'}`);
			return;
		}

		// Swap sections in the array
		const newSections = [...category.sections];
		[newSections[sectionIndex], newSections[newIndex]] = [newSections[newIndex], newSections[sectionIndex]];

		// Build the new fileContent
		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		// Update doc state
		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		// Update sidebar state
		setSidebarSections(prevSections => {
			const newSidebarSections = [...prevSections];
			const sidebarCategory = newSidebarSections[categoryIndex];
			if (sidebarCategory?.sections) {
				const newSidebarCategorySections = [...sidebarCategory.sections];
				[newSidebarCategorySections[sectionIndex], newSidebarCategorySections[newIndex]] =
					[newSidebarCategorySections[newIndex], newSidebarCategorySections[sectionIndex]];
				newSidebarSections[categoryIndex] = { ...sidebarCategory, sections: newSidebarCategorySections };
			}
			return newSidebarSections;
		});

		// Update editor content - swap the section divs
		const editor = editorRef.current;
		if (editor) {
			const editorBody = editor.getBody();
			const currentSection = editorBody.querySelector(`#${CSS.escape(sectionId)}`);
			const swapSection = editorBody.querySelector(`#${CSS.escape(newSections[sectionIndex].id)}`);

			if (currentSection && swapSection) {
				if (direction === 'up') {
					// Move current section before the swap section
					swapSection.parentNode.insertBefore(currentSection, swapSection);
				} else {
					// Move current section after the swap section
					swapSection.parentNode.insertBefore(currentSection, swapSection.nextSibling);
				}
			}
		}

		// Save directly (bypass reconciliation since this is a structural change)
		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`Section moved ${direction}`);
		} catch (error) {
			Toast.error('Failed to save section order');
		}
	};

	// Move subsection up/down within its parent section
	const handleMoveSubsection = async (categoryId, sectionId, subsectionId, direction) => {
		if (!doc?.fileContent?.categories) return;

		// Find the category
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const category = doc.fileContent.categories[categoryIndex];
		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;

		const section = category.sections[sectionIndex];
		if (!section.subsections) return;

		const subsectionIndex = section.subsections.findIndex(sub => sub.id === subsectionId);
		if (subsectionIndex === -1) return;

		// Calculate new index
		const newIndex = direction === 'up' ? subsectionIndex - 1 : subsectionIndex + 1;

		// Check bounds
		if (newIndex < 0 || newIndex >= section.subsections.length) {
			Toast.info(`Cannot move subsection ${direction} - already at the ${direction === 'up' ? 'top' : 'bottom'}`);
			return;
		}

		// Swap subsections in the array
		const newSubsections = [...section.subsections];
		[newSubsections[subsectionIndex], newSubsections[newIndex]] = [newSubsections[newIndex], newSubsections[subsectionIndex]];

		// Build the new fileContent
		const newSections = [...category.sections];
		newSections[sectionIndex] = { ...section, subsections: newSubsections };

		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		// Update doc state
		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		// Update sidebar state
		setSidebarSections(prevSections => {
			const newSidebarSections = [...prevSections];
			const sidebarCategory = newSidebarSections[categoryIndex];
			if (sidebarCategory?.sections) {
				const sidebarSection = sidebarCategory.sections[sectionIndex];
				if (sidebarSection?.subsections) {
					const newSidebarSubsections = [...sidebarSection.subsections];
					[newSidebarSubsections[subsectionIndex], newSidebarSubsections[newIndex]] =
						[newSidebarSubsections[newIndex], newSidebarSubsections[subsectionIndex]];

					const newSidebarCategorySections = [...sidebarCategory.sections];
					newSidebarCategorySections[sectionIndex] = { ...sidebarSection, subsections: newSidebarSubsections };
					newSidebarSections[categoryIndex] = { ...sidebarCategory, sections: newSidebarCategorySections };
				}
			}
			return newSidebarSections;
		});

		// Update editor content - swap the subsection divs within the parent section
		const editor = editorRef.current;
		if (editor) {
			const editorBody = editor.getBody();
			const parentSection = editorBody.querySelector(`#${CSS.escape(sectionId)}`);
			if (parentSection) {
				const currentSubsection = parentSection.querySelector(`#${CSS.escape(subsectionId)}`);
				const swapSubsection = parentSection.querySelector(`#${CSS.escape(newSubsections[subsectionIndex].id)}`);

				if (currentSubsection && swapSubsection) {
					if (direction === 'up') {
						swapSubsection.parentNode.insertBefore(currentSubsection, swapSubsection);
					} else {
						swapSubsection.parentNode.insertBefore(currentSubsection, swapSubsection.nextSibling);
					}
				}
			}
		}

		// Save directly (bypass reconciliation since this is a structural change)
		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`Subsection moved ${direction}`);
		} catch (error) {
			Toast.error('Failed to save subsection order');
		}
	};

	// Add optional section to the document (creates a copy from template)
	const handleAddSection = async (categoryId, sectionId) => {
		if (!doc?.fileContent?.categories) return;

		// Find the category and template section
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const category = doc.fileContent.categories[categoryIndex];
		const templateSectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (templateSectionIndex === -1) return;

		const templateSection = category.sections[templateSectionIndex];

		// Check if section is actually a template (required: false, optional: true, isVisible: true)
		if (templateSection.required !== false || templateSection.optional !== true) {
			Toast.info('This section is already part of the document');
			return;
		}

		// Generate a unique ID for the new section copy
		// Pattern: originalId-added-timestamp (we can extract originalId later by splitting on '-added-')
		const newSectionId = `${sectionId}-added-${Date.now()}`;

		// Create a duplicate section with new properties
		// Note: We don't store templateSectionId in backend - we derive it from the ID pattern
		const duplicatedSection = {
			...templateSection,
			id: newSectionId,
			required: false,
			optional: false,
			isVisible: true,
			// Deep clone subsections with new IDs
			subsections: (templateSection.subsections || []).map((sub, idx) => ({
				...sub,
				id: `${sub.id}-added-${Date.now()}-${idx}`
			}))
		};

		// Hide the template section (set isVisible: false)
		const hiddenTemplateSection = {
			...templateSection,
			isVisible: false
		};

		// Build new sections array: update template + add duplicate after it
		const newSections = [...category.sections];
		newSections[templateSectionIndex] = hiddenTemplateSection;
		// Insert the duplicated section right after the template
		newSections.splice(templateSectionIndex + 1, 0, duplicatedSection);

		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		// Update doc state
		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		// Update sidebar state
		setSidebarSections(prevSections => {
			const newSidebarSections = [...prevSections];
			const sidebarCategory = newSidebarSections[categoryIndex];

			if (sidebarCategory) {
				// Create new section object for sidebar (with delete capability)
				// canDelete is true because the ID contains '-added-' pattern
				const newSidebarSection = {
					id: newSectionId,
					title: duplicatedSection.title,
					canDelete: true, // Added sections can be deleted
					subsections: (duplicatedSection.subsections || [])
						.filter((sub) => sub.optional === false || sub.optional === undefined)
						.map((sub) => ({
							id: sub.id,
							title: sub.title
						}))
				};

				// Append at the end of sections (added sections go at the end)
				const newIncludedSections = [
					...(sidebarCategory.sections || []),
					newSidebarSection
				];

				// Remove from optional sections (template is now hidden)
				const newOptionalSections = (sidebarCategory.optionalSections || [])
					.filter(s => s.id !== sectionId);

				newSidebarSections[categoryIndex] = {
					...sidebarCategory,
					sections: newIncludedSections,
					optionalSections: newOptionalSections
				};
			}

			return newSidebarSections;
		});

		// Build HTML for the new section and insert in editor
		const editor = editorRef.current;
		if (editor) {
			let sectionHtml = `<div id="${newSectionId}">
				${duplicatedSection.content?.html || '<p><br></p>'}
			`;

			// Include subsections
			if (duplicatedSection.subsections && Array.isArray(duplicatedSection.subsections)) {
				duplicatedSection.subsections.forEach((sub) => {
					if (sub.optional === false || sub.optional === undefined) {
						sectionHtml += `
							<div id="${sub.id}">
								${sub.content?.html || '<p><br></p>'}
							</div>
						`;
					}
				});
			}

			sectionHtml += `</div>`;

			const editorBody = editor.getBody();
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = sectionHtml;
			const newSectionElement = tempDiv.firstChild;

			// Determine correct insertion index
			const visibleSections = [];
			newCategories.forEach(cat => {
				if (cat.sections) {
					cat.sections.forEach(sec => {
						if (sec.isVisible !== false) {
							visibleSections.push(sec.id);
						}
					});
				}
			});

			const newSectionIndex = visibleSections.indexOf(newSectionId);
			let inserted = false;

			if (newSectionIndex > 0) {
				const prevSectionId = visibleSections[newSectionIndex - 1];
				const prevElement = editorBody.querySelector(`#${CSS.escape(prevSectionId)}`);
				if (prevElement) {
					prevElement.parentNode.insertBefore(newSectionElement, prevElement.nextSibling);
					inserted = true;
				}
			} else if (newSectionIndex === 0) {
				if (editorBody.firstChild) {
					editorBody.insertBefore(newSectionElement, editorBody.firstChild);
					inserted = true;
				}
			}

			if (!inserted) {
				// Fallback: Append at the end of the editor content
				editorBody.appendChild(newSectionElement);
			}
		}

		// Reinitialize the previous content ref for change detection
		initializePreviousContentFromEditor();

		// Save directly to IndexedDB and sync to server
		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`"${duplicatedSection.title}" added to document`);
		} catch (error) {
			Toast.error('Failed to add section');
		}
	};

	// Delete an added section (restore the template)
	const handleDeleteSection = async (categoryId, sectionId) => {
		if (!doc?.fileContent?.categories) return;

		// Find the category and the added section
		const categoryIndex = doc.fileContent.categories.findIndex(c => c.id === categoryId);
		if (categoryIndex === -1) return;

		const category = doc.fileContent.categories[categoryIndex];
		const sectionIndex = category.sections.findIndex(s => s.id === sectionId);
		if (sectionIndex === -1) return;

		const sectionToDelete = category.sections[sectionIndex];

		// Check if this section can be deleted
		// Added sections have ID pattern: originalId-added-timestamp
		const isAddedSection = sectionId.includes('-added-');
		if (sectionToDelete.required === true || !isAddedSection) {
			Toast.error('This section cannot be deleted');
			return;
		}

		// Extract template section ID from the added section's ID pattern
		const templateSectionId = sectionId.split('-added-')[0];

		// Find the template section and restore its visibility
		const templateSectionIndex = category.sections.findIndex(s => s.id === templateSectionId);

		// Build new sections array
		const newSections = category.sections.filter(s => s.id !== sectionId);

		// Restore template visibility if found
		if (templateSectionIndex !== -1) {
			const templateSection = category.sections[templateSectionIndex];
			const restoredTemplate = {
				...templateSection,
				isVisible: true
			};
			// Find the index in newSections (it shifted after removing the deleted section)
			const newTemplateIndex = newSections.findIndex(s => s.id === templateSectionId);
			if (newTemplateIndex !== -1) {
				newSections[newTemplateIndex] = restoredTemplate;
			}
		}

		const newCategories = [...doc.fileContent.categories];
		newCategories[categoryIndex] = { ...category, sections: newSections };
		const newFileContent = { ...doc.fileContent, categories: newCategories };

		// Update doc state
		setDoc(prevDoc => ({
			...prevDoc,
			fileContent: newFileContent
		}));

		// Update sidebar state
		setSidebarSections(prevSections => {
			const newSidebarSections = [...prevSections];
			const sidebarCategory = newSidebarSections[categoryIndex];

			if (sidebarCategory) {
				// Remove the deleted section from sidebar
				const newIncludedSections = (sidebarCategory.sections || [])
					.filter(s => s.id !== sectionId);

				// Restore template to optional sections if it exists
				const templateSection = category.sections.find(s => s.id === templateSectionId);
				let newOptionalSections = [...(sidebarCategory.optionalSections || [])];

				if (templateSection) {
					newOptionalSections.push({
						id: templateSectionId,
						title: templateSection.title
					});
				}

				newSidebarSections[categoryIndex] = {
					...sidebarCategory,
					sections: newIncludedSections,
					optionalSections: newOptionalSections
				};
			}

			return newSidebarSections;
		});

		// Remove the section from the editor
		const editor = editorRef.current;
		if (editor) {
			const editorBody = editor.getBody();
			const sectionElement = editorBody.querySelector(`#${sectionId}`);
			if (sectionElement) {
				sectionElement.remove();
			}
		}

		// Reinitialize the previous content ref for change detection
		initializePreviousContentFromEditor();

		// Save directly to IndexedDB and sync to server
		try {
			await saveDocumentStructure(newFileContent);
			Toast.success(`"${sectionToDelete.title}" removed from document`);
		} catch (error) {
			Toast.error('Failed to remove section');
		}
	};

	// --- Section Preview ---
	const handlePreviewSection = (sectionId) => {
		const editor = editorRef.current;
		if (!editor) return;

		// Locate the DOM node
		const dom = editor.dom;
		const node = dom.get(sectionId);

		if (!node) {
			Toast.error('Section content not found');
			return;
		}

		// Save original getContent
		const originalGetContent = editor.getContent.bind(editor);

		// Override getContent to return only the section HTML
		// The preview plugin calls getContent() to get the HTML to display
		editor.getContent = () => {
			return node.outerHTML;
		};

		try {
			// Trigger the native preview command
			// This will call our overridden getContent, generate the preview HTML with styles, and open the dialog
			editor.execCommand('mcePreview');
		} catch (e) {
			console.error('Preview failed', e);
			Toast.error('Failed to open preview');
		} finally {
			// Restore original getContent immediately
			// This ensures subsequent operations (like autosave) use the correct full content
			editor.getContent = originalGetContent;
		}
	};

	// --- Sample Content Preview ---
	// --- Sample Content Preview ---
	const handleSampleContent = (itemId) => {
		const editor = editorRef.current;
		if (!editor) return;

		// Find the content in the doc state
		let foundContent = '';
		let found = false;

		if (doc?.fileContent?.categories) {
			for (const category of doc.fileContent.categories) {
				if (category.sections) {
					for (const section of category.sections) {
						// 1. Check if the ID matches a MAIN SECTION
						if (section.id === itemId) {
							// Start with the section's own sample content
							if (section.sampleContent && typeof section.sampleContent === 'object' && section.sampleContent.html) {
								foundContent += section.sampleContent.html;
							} else if (typeof section.sampleContent === 'string') {
								foundContent += section.sampleContent;
							}

							// Append all subsections' sample content
							if (section.subsections && Array.isArray(section.subsections)) {
								for (const sub of section.subsections) {
									if (sub.sampleContent && typeof sub.sampleContent === 'object' && sub.sampleContent.html) {
										foundContent += sub.sampleContent.html;
									} else if (typeof sub.sampleContent === 'string') {
										foundContent += sub.sampleContent;
									}
								}
							}
							found = true;
							break;
						}

						// 2. Check if the ID matches a SUBSECTION
						if (section.subsections) {
							const subsection = section.subsections.find(sub => sub.id === itemId);
							if (subsection) {
								if (subsection.sampleContent && typeof subsection.sampleContent === 'object' && subsection.sampleContent.html) {
									foundContent = subsection.sampleContent.html;
								} else if (typeof subsection.sampleContent === 'string') {
									foundContent = subsection.sampleContent;
								}
								found = true;
								break;
							}
						}
					}
				}
				if (found) break; // content found
			}
		}

		if (!found || !foundContent) {
			Toast.info('No sample content available for this section');
			return;
		}

		// Reuse the preview strategy: override getContent
		const originalGetContent = editor.getContent.bind(editor);

		editor.getContent = () => {
			return foundContent;
		};

		try {
			editor.execCommand('mcePreview');
		} catch (e) {
			console.error('Sample content preview failed', e);
			Toast.error('Failed to open sample content preview');
		} finally {
			editor.getContent = originalGetContent;
		}
	};

	const handlePreviewPDF = () => {
		if (editorRef.current) {
			editorRef.current.execCommand('mcePreview');
		}
	};

	const handleExportPDF = () => {
		if (editorRef.current) {
			editorRef.current.execCommand('ExportPDF');
		}
	};

	const handleDownloadPreviewPdf = () => {
		if (!pdfBlobForPreview) return;
		const url = URL.createObjectURL(pdfBlobForPreview);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${documentTitle || 'document'}.pdf`;
		link.click();
		URL.revokeObjectURL(url);
	};

	const handlePrintPreviewPdf = () => {
		if (!pdfBlobForPreview) return;
		const url = URL.createObjectURL(pdfBlobForPreview);
		const iframe = document.createElement('iframe');
		iframe.style.position = 'fixed';
		iframe.style.opacity = '0';
		iframe.src = url;
		document.body.appendChild(iframe);
		iframe.onload = () => {
			iframe.contentWindow?.focus();
			iframe.contentWindow?.print();
			setTimeout(() => {
				document.body.removeChild(iframe);
				URL.revokeObjectURL(url);
			}, 1000);
		};
	};

	// Citation Handlers
	const handleAddCitation = async (citationData) => {
		try {
			const { category, source, formData } = citationData;

			if (!category || !source || !formData) {
				Toast.error('Invalid citation data');
				throw new Error('Invalid citation data'); // Throw error so modal can catch it
			}

			// Check if we're editing an existing citation
			if (editingCitation && editingCitation._id) {
				const result = await CitationService.updateCitation(
					editingCitation._id,
					category,
					source,
					formData,
					documentId
				);

				if (result.citation) {
					setCitations((prev) =>
						prev.map((c) => (c._id === editingCitation._id ? result.citation : c))
					);
					Toast.success('Citation updated successfully');

					// Close modal only on success
					handleCloseCitationModal();
				} else {
					// If no citation in result, something went wrong
					Toast.error('Failed to update citation');
					throw new Error('Failed to update citation');
				}
			} else {
				// Creating a new citation
				const result = await CitationService.createCitation(category, source, formData, documentId);

				if (result.citation) {
					setCitations((prev) => [...prev, result.citation]);
					Toast.success('Citation added successfully');

					// Close modal only on success
					handleCloseCitationModal();
				} else {
					// If no citation in result, something went wrong
					Toast.error('Failed to create citation');
					throw new Error('Failed to create citation');
				}
			}
		} catch (error) {
			const message = error?.response?.data?.message || error?.message || 'Failed to save citation';
			Toast.error(message);
			// Re-throw the error so the modal can catch it and reset isSubmitting state
			throw error;
		}
	};

	const handleCitationEdit = (citation) => {
		setEditingCitation(citation);
		setCitationModalMode('edit');
		setIsCitationModalOpen(true);
	};
	const handleCitationDelete = (citation) => {
		setCitationToDelete(citation);
		setIsDeleteModalOpen(true);
	};
	const handleConfirmDelete = async () => {
		if (!citationToDelete) return;

		setIsDeletingCitation(true);
		try {
			const citationId = citationToDelete._id || citationToDelete.id;

			await CitationService.deleteCitation(citationId);

			setCitations((prev) => prev.filter((c) => (c._id || c.id) !== citationId));

			Toast.success('Citation deleted successfully');
			setIsDeleteModalOpen(false);
			setCitationToDelete(null);
			setSelectedCitation(null);
		} catch (error) {
			const message = error?.response?.data?.message || 'Failed to delete citation';
			Toast.error(message);
		} finally {
			setIsDeletingCitation(false);
		}
	};
	const handleCancelDelete = () => {
		setIsDeleteModalOpen(false);
		setCitationToDelete(null);
	};
	const handleCitationInsert = (citation) => {
		setCitationToInsert(citation);
		setCitationModalMode('insert');
		setIsCitationModalOpen(true);

		// Close the detail modal if it's open
		if (isViewDetailsModalOpen) {
			setIsViewDetailsModalOpen(false);
			setViewingCitationDetails(null);
		}
	};
	const handleInsertCitationSubmit = (insertData) => {
		console.log('Insert citation data:', insertData);

		const { citation, style, type, quote: rawQuote, locator, locatorType } = insertData;

		// Strip trailing period from quote if present (User Request)
		const quote = rawQuote ? rawQuote.trim().replace(/\.+$/, '') : '';

		// Step 1: Get the TinyMCE editor instance
		const editor = editorRef.current;
		if (!editor) {
			Toast.error('Editor not ready');
			return;
		}

		// Check if editor is in design mode (editable)
		if (editor.mode.get() === 'readonly') {
			Toast.error('Editor is not editable');
			return;
		}

		// Step 2: Generate the in-text citation
		const { mainCategory, category, subcategory, sourceType } = citation;
		const frontendSourceType = getFrontendSourceType(subcategory || sourceType, citation);
		const formFields = mapBackendToFormFields(citation);
		const inTextCitations = generateInText(
			mainCategory || category,
			frontendSourceType,
			formFields
		);
		// Step 3: Format the locator (auto-format: "23" → "p. 23")
		const formattedLocator = locator ? formatLocatorValue(locatorType, locator) : '';

		// Step 4: Build the final citation text based on style and type
		let textToInsert = '';

		if (style === 'Paraphrase') {
			// Simple paraphrase citation (no quote)
			textToInsert =
				type === 'Parenthetical' ? inTextCitations.parenthetical : inTextCitations.narrative;
		} else {
			// Direct Quote
			const wordCount = quote ? quote.trim().split(/\s+/).filter(w => w.length > 0).length : 0;
			const isBlockQuote = wordCount >= 40;

			if (isBlockQuote) {
				// Block Quote Formatting (> 40 words)
				// APA 7th: Indented, no quote marks, citation at the end.
				if (type === 'Parenthetical') {
					// Format: [Block Quote] (Author, Year, p. xx)
					const locatorPart = formattedLocator ? `, ${formattedLocator}` : '';
					const citationWithLocator = inTextCitations.parenthetical.replace(')', `${locatorPart})`);

					// Use inline style for simple indentation compatibility
					textToInsert = `<blockquote style="margin-left: 40px; border-left: none; padding-left: 0;">${quote} <span class="citation">${citationWithLocator}</span></blockquote>`;
				} else {
					// Narrative: Author (Year) ... [Block Quote] ... (p. xx)
					// We'll insert an intro line then the block quote
					const narrativeParts = inTextCitations.narrative.split('(');
					const author = narrativeParts[0].trim();
					const yearMatch = inTextCitations.narrative.match(/\(([^)]+)\)/);
					const year = yearMatch ? yearMatch[1] : 'n.d.';

					const locatorPart = formattedLocator ? ` (${formattedLocator})` : '';

					textToInsert = `<p>${author} (${year}) stated:</p><blockquote style="margin-left: 40px; border-left: none; padding-left: 0;">${quote} <span class="citation">${locatorPart}</span></blockquote>`;
				}
			} else {
				// Short Direct Quote (< 40 words)
				if (type === 'Parenthetical') {
					// Parenthetical direct quote: "quote text" (Author, 2020, p. 23)
					const locatorPart = formattedLocator ? `, ${formattedLocator}` : '';
					const citationWithLocator = inTextCitations.parenthetical.replace(')', `${locatorPart})`);
					textToInsert = quote ? `"${quote}" ${citationWithLocator}` : citationWithLocator;
				} else {
					// Narrative direct quote: Author (2020), "quote text" (p. 23).
					const narrativeParts = inTextCitations.narrative.split('(');
					const author = narrativeParts[0].trim();
					const yearMatch = inTextCitations.narrative.match(/\(([^)]+)\)/);
					const year = yearMatch ? yearMatch[1] : 'n.d.';

					if (quote && formattedLocator) {
						textToInsert = `${author} (${year}), "${quote}" (${formattedLocator}).`;
					} else if (quote) {
						textToInsert = `${author} (${year}), "${quote}".`;
					} else {
						textToInsert = inTextCitations.narrative;
					}
				}
			}
		}

		// Validate text
		if (!textToInsert || textToInsert.trim().length === 0) {
			console.error('❌ Generated text is empty!');
			Toast.error('Failed to generate citation text');
			return;
		}

		// Step 5: Insert the text at cursor position
		try {
			// Focus the editor first
			editor.focus();

			// If we have a saved bookmark, restore it
			if (savedCursorPosition) {
				editor.selection.moveToBookmark(savedCursorPosition);
			}

			// Check if we need to add a space before the citation
			const selection = editor.selection;
			const rng = selection.getRng();
			let needsSpace = false;

			if (rng.startOffset > 0) {
				// Check character before cursor
				const container = rng.startContainer;
				if (container.nodeType === Node.TEXT_NODE) {
					const charBefore = container.textContent.charAt(rng.startOffset - 1);
					if (charBefore && charBefore !== ' ' && charBefore !== '\n') {
						needsSpace = true;
					}
				}
			}

			const finalTextToInsert = needsSpace ? ' ' + textToInsert : textToInsert;

			// Insert the text content at cursor position
			editor.insertContent(finalTextToInsert);

			// Manually trigger auto-save debounce since this is a programmatic change
			if (isInitialLoadComplete.current) {
				debouncedFn1();
			}
		} catch (error) {
			console.error('❌ Error inserting text:', error);
			Toast.error('Failed to insert citation: ' + error.message);
			return;
		}

		// Step 6: Clear saved position
		setSavedCursorPosition(null);

		// Close modal and show success
		handleCloseCitationModal();
		Toast.success('Citation inserted successfully');


		// --- Auto-Append to References Section ---
		try {
			// 1. Generate the full reference text
			const referenceText = generateCitationForDisplay(citation, false);

			if (!referenceText) return;

			// 2. Find the "References" section ID
			let referencesSectionId = null;
			let referencesSectionTitle = '';


			// Check category-based structure
			if (doc?.fileContent?.categories) {
				for (const cat of doc.fileContent.categories) {
					// Check sections in category
					const foundSection = cat.sections?.find(s => {
						const title = s.title?.trim().toLowerCase();
						return title === 'references' ||
							title === 'reference list' ||
							title === 'bibliography' ||
							title === 'works cited';
					});

					if (foundSection) {
						referencesSectionId = foundSection.id;
						referencesSectionTitle = foundSection.title;
						break;
					}
				}
			}
			// Fallback (old structure)
			else if (doc?.fileContent?.sections) {
				const foundSection = doc.fileContent.sections.find(s => {
					const title = s.title?.trim().toLowerCase();
					return title === 'references' ||
						title === 'reference list' ||
						title === 'bibliography' ||
						title === 'works cited';
				});
				if (foundSection) {
					referencesSectionId = foundSection.id;
					referencesSectionTitle = foundSection.title;
				}
			}

			// 3. If found, append to that section in TinyMCE
			if (referencesSectionId) {
				const sectionBody = editor.getBody().querySelector(`#${referencesSectionId}`);

				if (sectionBody) {
					// Check if this reference already exists to prevent duplicates
					const tempDiv = document.createElement('div');
					tempDiv.innerHTML = referenceText;
					const plainTextRef = tempDiv.textContent.trim();

					// Normalize function to strip whitespace/punctuation for comparison
					const normalize = (str) => {
						if (!str) return '';
						return str.replace(/[^\w\d]/g, '').toLowerCase();
					};

					const normalizedNewRef = normalize(plainTextRef);
					const normalizedCurrentContent = normalize(sectionBody.textContent);

					// Robust duplicate check: if the normalized text is in there, skip
					// Add length check to avoid false positives on very short strings
					const isDuplicate = normalizedNewRef.length > 5 && normalizedCurrentContent.includes(normalizedNewRef);

					if (!isDuplicate) {
						// Create a new paragraph for the reference entry
						const p = editor.dom.create('p', { class: 'reference-entry', style: 'margin-bottom: 8px; padding-left: 24px; text-indent: -24px;' }, referenceText);

						// Find the last p tag with actual content (not just <br> or empty)
						const paragraphs = Array.from(sectionBody.querySelectorAll('p'));
						let lastContentP = null;

						for (let i = paragraphs.length - 1; i >= 0; i--) {
							const para = paragraphs[i];

							// 1. Check text content
							if (para.textContent.trim().length > 0) {
								lastContentP = para;
								break;
							}

							// 2. Check strict HTML content (ignoring br tags and nbsp)
							const cleanHtml = para.innerHTML
								.replace(/<br\s*\/?>/gi, '') // Remove <br>
								.replace(/&nbsp;/g, '')      // Remove &nbsp;
								.trim();

							if (cleanHtml.length > 0) {
								lastContentP = para;
								break;
							}
						}

						if (lastContentP) {
							// Insert AFTER the last contentful paragraph
							editor.dom.insertAfter(p, lastContentP);
							console.log('Inserted citation after last content paragraph');
						} else {
							// Fallback: Just append to the section if no ANY content found
							editor.dom.add(sectionBody, p);
							console.log('Appended citation to end of section (no content found)');
						}

						console.log(`✅ Auto-appended reference to "${referencesSectionTitle}"`);
						Toast.success(`Added entry to ${referencesSectionTitle}`);

						// Trigger auto-save again to capture this structural change
						debouncedFn1();
					} else {
						console.log('ℹ️ Reference already exists in bibliography, skipping append.');
						Toast.info('Reference already exists in bibliography');
					}
				} else {
					console.warn(`References section ID ${referencesSectionId} found in data but not in DOM`);
					Toast.error('References section not found in editor');
				}
			} else {
				console.log('No "References" section found to append citation.');
				Toast.error('No "References" section found in document');
			}
		} catch (err) {
			console.error('Error auto-appending reference:', err);
		}
	};
	const handleCitationSelect = (citation) => {
		// Save cursor position using TinyMCE bookmark
		const editor = editorRef.current;
		if (editor) {
			try {
				// Save current selection as a bookmark
				const bookmark = editor.selection.getBookmark(2); // Type 2 = offset bookmark
				setSavedCursorPosition(bookmark);
			} catch (e) {
				// If selection save fails, continue without it
				console.warn('Could not save cursor position:', e);
			}
		}

		// Toggle selection - if same citation clicked, deselect it
		setSelectedCitation((prev) => (prev === citation.id ? null : citation.id));
	};
	const handleCitationViewDetails = (citation) => {
		setViewingCitationDetails(citation);
		setIsViewDetailsModalOpen(true);
	};
	const handleCloseCitationModal = () => {
		setIsCitationModalOpen(false);
		setEditingCitation(null);
		setCitationToInsert(null);
		setCitationModalMode('add');
		setSavedCursorPosition(null); // Clear saved position
	};
	const handleCloseViewDetailsModal = () => {
		setIsViewDetailsModalOpen(false);
		setViewingCitationDetails(null);
	};

	const handleDocumentUpdated = async () => {
		try {
			const res = await DocumentService.fetchDocumentById(documentId);
			const d = res?.document;
			if (d) {
				setDoc(d);
				docRef.current = d;
			}
		} catch (error) {
			console.error('Failed to refresh document:', error);
			Toast.error('Failed to refresh document');
		}
	};

	// --- Custom TOC Generator ---
	const generateTOCHtml = () => {
		const currentDoc = docRef.current;
		if (!currentDoc?.fileContent?.categories) return '';

		// Added contenteditable="false" to make it read-only/non-editable
		let tocHtml = '<div class="mce-toc" contenteditable="false">';
		tocHtml += '<h2>Table of Contents</h2>';
		tocHtml += '<ul>';

		currentDoc.fileContent.categories.forEach(category => {
			if (category.sections) {
				category.sections.forEach(section => {
					// User logic: "optional true and required false are the one that are like optional other all should eb in TOCC"
					// Meaning: Exclude if (optional == true && required == false)
					// Or conversely: Include if !(optional == true && required == false)
					const isOptionalAvailableOnly = (section.optional === true && section.required === false);

					if (!isOptionalAvailableOnly) {
						// Main Section Link
						tocHtml += `<li><a href="#${section.id}">${section.title || 'Untitled Section'}</a></li>`;

						// Subsections
						if (section.subsections && section.subsections.length > 0) {
							tocHtml += '<ul style="margin-left: 20px;">';
							section.subsections.forEach(sub => {
								// Apply same logic to subsections if they have these props, otherwise include
								// Assuming subsections inherit or just show all if parent is shown
								tocHtml += `<li><a href="#${sub.id}">${sub.title || 'Untitled Subsection'}</a></li>`;
							});
							tocHtml += '</ul>';
						}
					}
				});
			}
		});

		tocHtml += '</ul>';
		tocHtml += '</div>';

		return tocHtml;
	};

	const handleInsertProjectTOC = () => {
		const editor = editorRef.current;
		if (!editor) return;

		const tocHtml = generateTOCHtml();

		// Check if a TOC already exists to replace it, or insert new
		const existingToc = editor.dom.select('.mce-toc')[0];

		if (existingToc) {
			editor.dom.setOuterHTML(existingToc, tocHtml);
			// Toast.success('Table of Contents updated');
		} else {
			editor.insertContent(tocHtml);
			// Toast.success('Table of Contents inserted');
		}
	};

	// --- Navigation Confirmation Logic ---
	// 1. Browser Refresh/Close Protection
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (isCreated) {
				e.preventDefault();
				e.returnValue = ''; // Required for generic browser confirmation
				return '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isCreated]);

	// 2. Internal React Router Navigation Protection
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) => {
			// Block navigation only if:
			// 1. User is in "isCreated" mode (editor open)
			// 2. User is navigating to a DIFFERENT path
			if (isCreated && currentLocation.pathname !== nextLocation.pathname) {
				return true;
			}
			return false;
		}
	);

	// JSX
	return (
		<div className="h-screen flex flex-col bg-gray-50 relative">
			{/* Navigation Warning Modal */}
			{blocker.state === 'blocked' && (
				<ConfirmationModal
					isOpen={true}
					onClose={() => blocker.reset()}
					icon={
						<div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center">
							<AlertTriangle className="w-10 h-10 text-red-500" />
						</div>
					}
					heading="Exit Editor?"
					description="Are you sure you want to exit the editor?"
					primaryButtonText="Leave Editor"
					secondaryButtonText="Stay"
					onPrimaryClick={() => blocker.proceed()}
					onSecondaryClick={() => blocker.reset()}
				/>
			)}

			{(loading ) && (
				<Loader overlay text={'Loading document...'} />
			)}

			{/* Compute PDF Options based on current doc state */}
			{/* We do this inside the render or a useMemo */}

			<EditorTopBar
				title={documentTitle}
				DocumentType={documentType}
				status={documentStatus}
				editedTime={documentEditedTime}
				format={documentFormat}
				university={documentUniversity}
				isCreated={isCreated}
				documentId={documentId}
				savingStatus={savingStatus}
				manualSavingStatus={manualSavingStatus}
				onManualSave={handleManualSave}
				onEditDocumentClick={handleEditDocumentClick}
				onDocumentUpdated={handleDocumentUpdated}
				onPreviewPDF={handlePreviewPDF}
				onExportPDF={handleExportPDF}
			/>

			<div className="flex flex-1">
				{/* === LEFT SIDEBAR === */}
				<div className="w-[20%] bg-white border-0 sticky top-0 h-screen">
					<EditorSidebarSection
						sections={sidebarSections}
						onNavigate={handleSidebarNavigate}
						onMoveSection={handleMoveSection}
						onMoveSubsection={handleMoveSubsection}
						onAddSection={handleAddSection}
						onDeleteSection={handleDeleteSection}
						onPreviewSection={handlePreviewSection}
						onSampleContent={handleSampleContent}
					/>
				</div>

				{/* === CENTER EDITOR === */}
				<div
					id="tinymce-editor-wrapper"
					className={`flex flex-col transition-all duration-300 ease-in-out ${showCitationManager ? 'w-[60%]' : 'w-[80%]'
						}`}
				>
					{/* Only render editor when we have the doc (and thus title/type) to ensure config is correct */}
					{doc && (
						<Editor
							apiKey={TINYMCE_API_KEY}
							onInit={(evt, editor) => {
								editorRef.current = editor;
								setEditorReady(true);
								// Set initial readonly state based on isCreated
								if (!isCreated) {
									editor.mode.set('readonly');
								}

								// OVERRIDE Native TOC Commands
								// This hijacks the "Insert Table of Contents" button and menu item
								editor.addCommand('mceInsertToc', () => {
									handleInsertProjectTOC();
								});
								editor.addCommand('mceUpdateToc', () => {
									handleInsertProjectTOC();
								});
							}}
							onEditorChange={handleEditorChange}
							init={{
								height: 'calc(100vh - 80px)',
								menubar: true,

								menu: {
									file: {
										title: 'File',
										// Everything EXCEPT "newdocument"
										items: 'preview print | exportpdf'
									}
								},
								plugins: [
									'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
									'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
									'insertdatetime', 'table', 'help', 'wordcount', 'tableofcontents',
									'pagebreak', 'nonbreaking',
									// Added based on request
									'exportpdf', 'nonbreaking',
								],
								selector: 'textarea',
								nonbreaking_force_tab: true,
								toolbar: 'undo redo | blocks fontfamily fontsize | ' +
									'bold italic underline forecolor | alignleft aligncenter ' +
									'alignright alignjustify | bullist numlist outdent indent | ' +
									'lineheight | table link image | exportpdf | tableofcontents | pagebreak | removeformat | help',
								font_family_formats: 'Arial=arial,helvetica,sans-serif; Times New Roman=times new roman,times,serif; Georgia=georgia,palatino,serif; Verdana=verdana,geneva,sans-serif; Calibri=calibri,sans-serif; Helvetica=helvetica,sans-serif; Courier New=courier new,courier,monospace; Trebuchet MS=trebuchet ms,geneva,sans-serif; Roboto=roboto,sans-serif; Open Sans=open sans,sans-serif; Lato=lato,sans-serif; Montserrat=montserrat,sans-serif; Poppins=poppins,sans-serif',
								font_size_formats: '8pt 9pt 10pt 12pt 14pt 16pt 20pt 24pt 32pt 42pt 54pt 68pt 84pt 98pt',
								line_height_formats: '1 1.15 1.5 1.75 2 2.5 3',
								// Pagebreak configuration
								pagebreak_separator: '<div class="mce-pagebreak" style="page-break-after: always; break-after: page;"></div>',
								pagebreak_split_block: true,

								// TOC Configuration
								tableofcontents_class: 'mce-toc',
								tableofcontents_header: 'h2', // Header tag for TOC title
								content_css: false,
								content_style: `
								body.mce-content-body { 
									font-family: "Times New Roman", Times, serif; 
									font-size: 12px; 
									line-height: 2;
								}

								/* Screen View: Paper Simulation */
								@media screen {
									body.mce-content-body {
										margin: 0 auto !important;
										padding: 96px !important; /* 1 inch margins */
										max-width: 794px; /* A4 width at 96 DPI */
										min-height: 1123px; /* A4 height at 96 DPI */
										box-sizing: border-box;
										background: #ffffff;
										box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
									}
								}

								/* Print/PDF overrides - Ensure clean export */
								@media print {
									body.mce-content-body {
										box-shadow: none !important;
										background: transparent !important;
										margin: 0 !important;
										padding: 0 !important;
										max-width: 100% !important;
										min-height: auto !important;
									}
									
									.mce-pagebreak {
										border: 0;
										height: 0;
										margin: 0;
										page-break-after: always;
										break-after: page;
									}
								}

								/* Fallback if exporter ignores media print but allows specific class overrides */
								body.mce-content-body.mce-exportpdf {
									box-shadow: none !important;
									background: transparent !important;
									margin: 0 !important;
									padding: 0 !important;
									max-width: 100% !important;
								}

								[id^="section-"], [id^="subsection-"] {
									display: block;
								}

								p, h1, h2, h3, h4, h5, h6 {
									margin: 0 0 0 0;
									padding: 0;
								}

								p {
									text-indent: 0;
								}

								/* Pagebreak visual styling */
								.mce-pagebreak {
									cursor: default;
									display: block;
									border: 0;
									width: 100%;
									height: 5px;
									border: 1px dashed #666;
									margin: 15px 0;
									page-break-after: always;
									break-after: page;
								}

								/* Ensure images adapt to page width */
								img {
									max-width: 100%;
									height: auto;
								}

								.page-break-before {
									page-break-before: always;
									break-before: page;
								}

								/* Table of Contents Customization */
								.mce-toc {
									border: 0 !important;
									padding: 0 !important;
									background-color: transparent !important;
								}

								.mce-toc h2 {
									font-family: "Times New Roman", Times, serif;
									font-weight: bold;
									text-align: center;
									margin-bottom: 1rem;
									font-size: 12pt; /* Reasonable default for title */
								}

								.mce-toc ul, .mce-toc li {
									list-style-type: none !important;
									margin: 0;
									padding: 0;
								}

								.mce-toc a {
									text-decoration: none !important;
									color: #000000 !important;
									font-family: "Times New Roman", Times, serif;
									font-size: 12pt;
									line-height: 2;
									text-align: left;
									display: block;
								}

								.mce-toc a:hover {
									text-decoration: none !important;
									background-color: transparent !important;
								}`,
								placeholder: 'Start typing your document...',
								branding: false,
								promotion: false,
								resize: false,
								statusbar: false,
								elementpath: false,

								// --- New Configuration ---
								autocorrect_capitalize: true,
								typography_default_lang: 'en-US',
								typography_langs: ['en-US', 'es', 'de'],
								typography_rules: [
									'common/punctuation/quote',
									'en-US/dash/main',
									'common/nbsp/afterParagraphMark',
									'common/nbsp/afterSectionMark',
									'common/nbsp/afterShortWord',
									'common/nbsp/beforeShortLastNumber',
									'common/nbsp/beforeShortLastWord',
									'common/nbsp/dpi',
									'common/punctuation/apostrophe',
									'common/space/delBeforePunctuation',
									'common/space/afterComma',
									'common/space/afterColon',
									'common/space/afterExclamationMark',
									'common/space/afterQuestionMark',
									'common/space/afterSemicolon',
									'common/space/beforeBracket',
									'common/space/bracket',
									'common/space/delBeforeDot',
									'common/space/squareBracket',
									'common/number/mathSigns',
									'common/number/times',
									'common/number/fraction',
									'common/symbols/arrow',
									'common/symbols/cf',
									'common/symbols/copy',
									'common/punctuation/delDoublePunctuation',
									'common/punctuation/hellip'
								],
								typography_ignore: ['code'],


								// Dynamic PDF Export Options
								exportpdf_converter_options: getExportPdfOptions(documentTitle, documentType),

								// exportpdf_token_provider fetches a token from the backend
								exportpdf_token_provider: () => {
									return axiosInstance.post(`${API_BASE_URL}/tinymce/jwt`)
										.then(response => response.data);
								},

								// Enable Image Upload Tab (User will configure S3 logic here)
								// Enable Image Upload Tab with Custom Backend Logic
								images_upload_handler: (blobInfo, progress) => new Promise(async (resolve, reject) => {
									try {
										const file = blobInfo.blob();

										// 1. Identify current section ID
										let sectionId = 'general';
										const editor = editorRef.current;
										if (editor) {
											// Determine valid section ID from selection
											const node = editor.selection.getNode();
											const sectionNode = editor.dom.getParent(node, '[id^="section-"], [id^="subsection-"]');
											if (sectionNode) {
												sectionId = sectionNode.id;
											}
										}

										// 2. Prepare FormData
										const formData = new FormData();
										formData.append('image', file, blobInfo.filename());
										formData.append('sectionId', sectionId);

										// 3. Upload via DocumentService with progress callback
										const response = await DocumentService.uploadDocumentImage(
											documentId,
											formData,
											(percent) => {
												// Update TinyMCE progress
												// Check if progress is a function (DocumentService safeguard)
												if (typeof progress === 'function') {
													progress(percent);
												}
											}
										);

										if (response && response.url) {
											resolve(response.url);
										} else {
											reject('Upload failed: Invalid response from server');
										}
									} catch (error) {
										console.error('Image Upload Error:', error);
										const message = error?.response?.data?.message || error.message || 'Image upload failed';
										reject(message);
									}
								}),

								setup: (editor) => {
									// Track cursor position changes for citation insertion
									editor.on('NodeChange', (e) => {
										try {
											const bookmark = editor.selection.getBookmark(2);
											setSavedCursorPosition(bookmark);

											// Auto-set default dimensions for images if missing (User Request)
											// preventing 'empty' width/height which might be annoying for resizing
											if (e.element && e.element.tagName === 'IMG') {
												const img = e.element;
												// If width/height attributes are missing, set them to show logical defaults
												// TinyMCE usually handles this, but we force it if requested.
												// We check for both attribute and style to avoid overwriting invalid logic.
												if (!img.getAttribute('width') && !img.style.width) {
													// Default to a reasonable size or 100% if fitting context
													// Using 300px as a safe default that isn't too huge or too small
													editor.dom.setAttrib(img, 'width', '300');
													// Ensure height is auto to maintain aspect ratio
													if (!img.getAttribute('height') && !img.style.height) {
														editor.dom.setAttrib(img, 'height', 'auto');
													}
												}
											}
										} catch (e) {
											// Ignore errors
										}
									});

									// Also listen for ObjectResized to ensure dimensions persist if needed
									editor.on('ObjectResized', (e) => {
										// Could enforce constraints here if needed
									});
								}
							}}
						/>
					)}
				</div>

				{/* === RIGHT SIDEBAR === */}
				{/* Right column */}
				{showCitationManager && (
					<div
						className={`w-[20%] bg-white border-0 sticky top-0 h-screen  transition-all duration-300 ease-in-out ${showCitationManager ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
							}`}
					>
						{/* Right Column: Citation Cards for testing */}
						<div className="space-y-3">
							<div className="">
								<div className="flex items-center justify-between py-5 px-3">
									<h2 className="text-25 font-semibold text-dark-blue leading-100">
										Source Library
									</h2>

									<button
										onClick={() => setIsSourceLibraryExpanded(!isSourceLibraryExpanded)}
										className="text-dark-blue hover:text-purple transition-colors"
									>
										<ChevronUp
											className={`w-6 h-6 transition-transform duration-300 ${isSourceLibraryExpanded ? '' : 'rotate-180'
												}`}
										/>
									</button>
								</div>

								<span className="block border-b border-lightGrey w-full"></span>
							</div>

							<div
								className={`p-4 transition-all duration-300 ease-in-out ${isSourceLibraryExpanded ? 'max-h-[2000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
									}`}
							>
								{/* Search Bar with Filter and Add Button */}
								<div className="flex items-center gap-1 mb-4">
									<div className="relative flex-1">
										<input
											type="text"
											placeholder="Search citations"
											value={query}
											onChange={(e) => setQuery(e.target.value)}
											className="h-[52px] w-full rounded-[12px] border border-[#F0F0F0] pl-1 xl:pr-16 pr-12 text-gray focus:outline-none focus:border-purple bg-white placeholder:text-[10px]"
										/>
										<div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
											{/* Clear Button - only show when there's text */}
											{query && (
												<button
													onClick={() => setQuery('')}
													className="text-gray hover:text-dark-blue transition-colors"
													title="Clear search"
												>
													<svg
														width="16"
														height="16"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
												</button>
											)}
											{/* Search Icon */}
											<button className="text-dark-blue hover:text-purple transition-colors">
												<svg
													width="16"
													height="16"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<circle cx="11" cy="11" r="8"></circle>
													<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
												</svg>
											</button>
											{/* Filter Icon */}
											<div className="relative inline-block group">
												<Tooltip content="Filter sources" placement="left">
													<button className="text-dark-blue hover:text-purple transition-colors">
														<svg
															width="16"
															height="16"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														>
															<line x1="4" y1="21" x2="4" y2="14"></line>
															<line x1="4" y1="10" x2="4" y2="3"></line>
															<line x1="12" y1="21" x2="12" y2="12"></line>
															<line x1="12" y1="8" x2="12" y2="3"></line>
															<line x1="20" y1="21" x2="20" y2="16"></line>
															<line x1="20" y1="12" x2="20" y2="3"></line>
															<line x1="1" y1="14" x2="7" y2="14"></line>
															<line x1="9" y1="8" x2="15" y2="8"></line>
															<line x1="17" y1="16" x2="23" y2="16"></line>
														</svg>
													</button>
												</Tooltip>
											</div>
										</div>
									</div>
									{/* Add Button */}
									<div className="relative inline-block group">
										<Tooltip content="Add source" placement="left">
											<button
												onClick={() => {
													setCitationModalMode('add');
													setIsCitationModalOpen(true);
												}}
												className="h-[52px] w-[52px] flex items-center justify-center rounded-[12px] border border-[#F0F0F0] bg-white hover:border-purple transition-colors shrink-0"
											>
												<svg
													width="20"
													height="20"
													viewBox="0 0 20 20"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M10 4V16M4 10H16"
														stroke="#180A63"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</button>
										</Tooltip>
									</div>
								</div>

								{/* Citation Cards */}
								<div className="space-y-3">
									{filteredCitations.map((citation) => (
										<CitationCard
											key={citation._id || citation.id}
											citation={citation}
											isSelected={selectedCitation === (citation._id || citation.id)}
											onSelect={handleCitationSelect}
											onEdit={handleCitationEdit}
											onDelete={handleCitationDelete}
											onInsert={handleCitationInsert}
											onViewDetails={handleCitationViewDetails}
										/>
									))}
									{citations.length === 0 && (
										<div className="text-center py-6 text-gray-500 text-sm">
											No citations yet. Click "Add source" to get started.
										</div>
									)}
									{citations.length > 0 && filteredCitations.length === 0 && (
										<div className="text-center py-6 text-gray-500 text-sm">
											No citations found matching "{query}".
										</div>
									)}
								</div>

								{/* Add Source Button */}
								<Button
									className="w-full mt-4"
									onClick={() => {
										setCitationModalMode('add');
										setIsCitationModalOpen(true);
									}}
								>
									<div className="flex flex-row items-center justify-center gap-2">
										<div>
											<svg
												width="17"
												height="18"
												viewBox="0 0 17 18"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M9.5625 4.375C9.5625 3.89102 9.17148 3.5 8.6875 3.5C8.20352 3.5 7.8125 3.89102 7.8125 4.375V8.3125H3.875C3.39102 8.3125 3 8.70352 3 9.1875C3 9.67148 3.39102 10.0625 3.875 10.0625H7.8125V14C7.8125 14.484 8.20352 14.875 8.6875 14.875C9.17148 14.875 9.5625 14.484 9.5625 14V10.0625H13.5C13.984 10.0625 14.375 9.67148 14.375 9.1875C14.375 8.70352 13.984 8.3125 13.5 8.3125H9.5625V4.375Z"
													fill="white"
												/>
											</svg>
										</div>
										<span>Add source</span>
									</div>
								</Button>
							</div>
						</div>
					</div>
				)}
			</div>

			<AddCitationModal
				isOpen={isCitationModalOpen}
				onClose={handleCloseCitationModal}
				onAddCitation={handleAddCitation}
				editingCitation={editingCitation}
				mode={citationModalMode}
				citationToInsert={citationToInsert}
				onInsertCitation={handleInsertCitationSubmit}
			/>

			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={handleCancelDelete}
				icon={<img src={DeleteIcon} alt="Delete" className="mx-auto" />}
				heading="Delete Citation?"
				description="Are you sure you want to delete this citation? This action cannot be undone."
				primaryButtonText="Cancel"
				secondaryButtonText="Delete"
				onPrimaryClick={handleCancelDelete}
				onSecondaryClick={handleConfirmDelete}
				isLoading={isDeletingCitation}
			/>
			<CitationDetailModal
				isOpen={isViewDetailsModalOpen}
				onClose={handleCloseViewDetailsModal}
				citation={viewingCitationDetails}
				onInsert={() => handleCitationInsert(viewingCitationDetails)}
			/>
			<PDFPreviewModal
				isOpen={isPDFPreviewOpen}
				onClose={() => {
					setIsPDFPreviewOpen(false);
					setPdfBlobForPreview(null);
				}}
				documentContent={pdfBlobForPreview}
				documentTitle={documentTitle}
				onDownload={handleDownloadPreviewPdf}
				onPrint={handlePrintPreviewPdf}
			/>
		</div>
	);
};

export default DocumentEditor;
